/**
 * launcherDispatch.ts
 *
 * LauncherPage の dispatch 決定ロジック (純粋関数)。
 * LauncherPage.tsx 本体は react-router-dom に依存するため、jest が v7 ESM を resolve できない問題を
 * 回避するために、ロジックだけここに分離して単体テスト可能にする。
 *
 * Codex 2026-05-16 仕様 + 2026-05-16 user fb (mixed 端末で直近 mode 優先):
 *   ① forceCorporate → fallback
 *   ② lastMode='individual' + 個別キー完全 → 個別 URL
 *   ③ lastMode='corporate' + 法人キー完全 → /c/:customerCode
 *   ④ lastMode 不一致 or 未設定 (legacy)
 *     ④a 個別キー完全 → 個別 URL
 *     ④b 法人キー完全 → /c/:customerCode
 *   ⑤ どれも該当せず → fallback
 *
 * 「直近に開いた mode を優先」ルールにより、user が法人 mode で URL 開いた後ホーム追加すれば
 * その後アイコンタップで法人 mode に飛ぶ (= 過去の個別キーで個別 mode に勝手に飛ばない)。
 */

import { IndividualLink, LastMode } from '../utils/customerCodeStorage';

export type DispatchDecision =
  | { type: 'navigate'; url: string }
  | { type: 'fallback' };

function individualUrl(link: IndividualLink): string | null {
  if (!link.tenantId || !link.customerCode) return null;
  if (link.accessToken) {
    return `/i/${link.tenantId}/${link.customerCode}/${link.accessToken}`;
  }
  if (link.shortCode) {
    return `/c/${link.tenantId}/${link.customerCode}/${link.shortCode}`;
  }
  return null;
}

function corporateUrl(corpCode: string, corpTenant: string): string | null {
  if (!corpCode || !corpTenant) return null;
  return `/c/${corpCode}`;
}

export function decideDispatch(
  forceCorporate: boolean,
  individualLink: IndividualLink | null,
  corpCode: string,
  corpTenant: string,
  lastMode: LastMode | null = null
): DispatchDecision {
  // ① force=corporate → 自動 dispatch スキップ
  if (forceCorporate) {
    return { type: 'fallback' };
  }

  const indvUrl = individualLink ? individualUrl(individualLink) : null;
  const corpUrlStr = corporateUrl(corpCode, corpTenant);

  // ② lastMode 優先: 直近に開いた mode のキーが揃ってればそっち
  if (lastMode === 'individual' && indvUrl) {
    return { type: 'navigate', url: indvUrl };
  }
  if (lastMode === 'corporate' && corpUrlStr) {
    return { type: 'navigate', url: corpUrlStr };
  }

  // ③ lastMode 不一致 or 未設定 (legacy 利用者) → 個別 > 法人 の従来順
  if (indvUrl) {
    return { type: 'navigate', url: indvUrl };
  }
  if (corpUrlStr) {
    return { type: 'navigate', url: corpUrlStr };
  }

  // ④ どれもなし → fallback
  return { type: 'fallback' };
}
